import { Component, OnInit } from '@angular/core';
import { LogService } from '../../../../../services/log/log.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
	DocumentUploadContractType,
	DocumentUploadContractTypeExtensions,
} from '../../../../../models/enums/document-upload-contract-type';
import { DocumentRecordValidationViewModel } from '../view-models/document-record-validation-view-model';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { AppContextService } from '../../../../../services/app-context.service';
import { User } from '../../../../../models/User/user';
import {
	UploadDocumentsCountCriteria,
	UploadDocumentsResult,
	UploadDocumentsSearchCriteria,
	UploadDocumentsValidationStatus,
} from '../../../../../server-api/upload-documents-api/upload-documents-api.models';
import { UploadDocumentsApiService } from '../../../../../server-api/upload-documents-api/upload-documents-api.service';
import { createTablePagination, TableFilter, TablePagination, TableSearchEvent } from '../../../../../shared-components/custom-table/custom-table.types';
import {
	CustomTableFilterMapperService,
	FilterMappingConfig,
} from '../../../../../shared-components/custom-table/custom-table-filter-mapper.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
	selector: 'app-documents-upload-step4',
	templateUrl: './documents-upload-step4.component.html',
	styleUrls: ['../documents-upload.scss'],
	standalone: false,
})
export class DocumentsUploadStep4Component implements OnInit {
	protected isPerformingRequest = false;
	protected uploadId: string;
	public invalidDocumentRecords: DocumentRecordValidationViewModel[] = [];
	protected totalValidRecords: number = 0;
	protected totalInvalidRecords: number = 0;
	private uploadContractType: DocumentUploadContractType | null = null;
	private fileRelatedContractId: string;
	private loggedUser: User | null = new User();

	public tablePagination: TablePagination = createTablePagination();
	public tableFilters: TableFilter[] = [];
	public isTableLoading = true;

	protected filterMappings: FilterMappingConfig[] = [
		{
			field: 'uploadedDocumentType',
			targetProperty: 'uploadedDocumentType',
			type: 'string',
		},
		{
			field: 'uploadedDocumentNumber',
			targetProperty: 'uploadedDocumentNumber',
			type: 'string',
		},
		{
			field: 'uploadedDocumentSupplier',
			targetProperty: 'uploadedDocumentSupplier',
			type: 'string',
		},
		{
			field: 'uploadedDocumentBuyer',
			targetProperty: 'uploadedDocumentBuyer',
			type: 'string',
		},
	];

	protected searchCriteria: UploadDocumentsSearchCriteria = new UploadDocumentsSearchCriteria();

	currentStep: number = 3;
	steps: Step[] = [];

	constructor(
		private logger: LogService,
		private route: ActivatedRoute,
		private router: Router,
		private translateService: TranslateService,
		private routeAuthorizationService: RouteAuthorizationService,
		private appContext: AppContextService,
		private uploadDocumentsApiService: UploadDocumentsApiService,
		private toasterService: ToasterService,
		protected filterMapper: CustomTableFilterMapperService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (this.canAccessStep4()) {
				this.route.params.subscribe((params) => {
					const uploadContractTypeRouteParam = params['uploadContractType'] || '';
					if (!!uploadContractTypeRouteParam) {
						this.uploadContractType = DocumentUploadContractTypeExtensions.FromRouteParam(uploadContractTypeRouteParam);
					}

					this.uploadId = params['uploadId'] || '';
					this.fileRelatedContractId = params['contractId'] || '';

					if (!this.uploadId || !this.uploadContractType || !this.fileRelatedContractId) {
						this.logger.error(
							'Missing params on documents upload step4.',
							this.uploadId,
							this.uploadContractType,
							this.fileRelatedContractId
						);
						this.toasterService.showError(
							this.translateService.translate('documents-upload-step4.missing-params-on-documents-upload-step4', 'client')
						);
						return;
					}

					if (this.uploadId) {
						this.initializeTableFilters();
						this.loadData();
					} else {
						this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_UPLOAD_ID_FROM_ROUTE', 'list-errorCodes'));
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	private loadData(): void {
		this.loadRecordCounts();
		this.loadUploadedDocuments();
	}

	private loadRecordCounts(): void {
		if (!this.uploadId) {
			return;
		}

		const validCountCriteria = new UploadDocumentsCountCriteria();
		validCountCriteria.fileUploadId = this.uploadId;
		validCountCriteria.validationStatusList = [UploadDocumentsValidationStatus.Valid];

		const invalidCountCriteria = new UploadDocumentsCountCriteria();
		invalidCountCriteria.fileUploadId = this.uploadId;
		invalidCountCriteria.validationStatusList = [UploadDocumentsValidationStatus.Invalid];

		forkJoin({
			validCount: this.uploadDocumentsApiService.uploadDocumentsCount(validCountCriteria),
			invalidCount: this.uploadDocumentsApiService.uploadDocumentsCount(invalidCountCriteria),
		})
			.pipe(
				finalize(() => {
					this.isPerformingRequest = false;
				})
			)
			.subscribe({
				next: ({ validCount, invalidCount }) => {
					if (validCount.IsValid) {
						this.totalValidRecords = validCount.Data ?? 0;
					}
					if (invalidCount.IsValid) {
						this.totalInvalidRecords = invalidCount.Data ?? 0;
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.logger.error('[DocumentsUploadStep4Component::loadRecordCounts] HttpErrorResponse: ', httpErrorResponse);
					this.toasterService.showError('Failed to load record counts', {
						description: httpErrorResponse.message,
					});
				},
			});
	}

	private loadUploadedDocuments(): void {
		this.searchCriteria.validationStatusList = [UploadDocumentsValidationStatus.Invalid];
		this.performSearch(0);
	}

	protected performSearch(pageNumber: number): void {
		if (!this.uploadId) {
			this.logger.error('[DocumentsUploadStep4Component::performSearch] uploadId is not set');
			this.isTableLoading = false;
			return;
		}

		this.isTableLoading = true;

		const uploadDocumentsSearchCriteria = new UploadDocumentsSearchCriteria();
		uploadDocumentsSearchCriteria.OrderBy = 'RowNumber';
		uploadDocumentsSearchCriteria.OrderDescending = false;
		uploadDocumentsSearchCriteria.PageNumber = pageNumber;
		uploadDocumentsSearchCriteria.RowsPerPage = this.tablePagination.pageSize;
		uploadDocumentsSearchCriteria.RestrictionCriteria = this.buildRestrictionCriteria();

		const countCriteria: UploadDocumentsCountCriteria = this.buildRestrictionCriteria();

		forkJoin({
			search: this.uploadDocumentsApiService.uploadDocumentsSearch(uploadDocumentsSearchCriteria),
			count: this.uploadDocumentsApiService.uploadDocumentsCount(countCriteria),
		})
			.pipe(
				finalize(() => {
					this.isTableLoading = false;
				})
			)
			.subscribe({
				next: ({ search, count }) => {
					if (search.IsValid) {
						this.invalidDocumentRecords = (search.Data || []).map((record) => this.toValidationViewModel(record));
					} else {
						this.invalidDocumentRecords = [];
						this.toasterService.showError('Error searching documents', {
							errors: search.Errors,
							autoClose: false,
						});
					}

					if (count.IsValid) {
						this.tablePagination.totalItems = count.Data ?? 0;
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.logger.error('[DocumentsUploadStep4Component::performSearch] HttpErrorResponse: ', httpErrorResponse);
					this.invalidDocumentRecords = [];
					this.toasterService.showError(httpErrorResponse.message);
				},
			});
	}

	protected onSearchRequested(event: TableSearchEvent): void {
		this.searchCriteria = new UploadDocumentsSearchCriteria();
		this.searchCriteria.OrderBy = 'RowNumber';
		this.searchCriteria.OrderDescending = false;
		this.searchCriteria.fileUploadId = this.uploadId;
		this.searchCriteria.validationStatusList = [UploadDocumentsValidationStatus.Invalid];

		if (this.filterMapper && event.filters?.length > 0) {
			this.filterMapper.mapFiltersToObject(event.filters, this.filterMappings, this.searchCriteria);
		}

		this.tablePagination.pageIndex = event.pageIndex;
		this.tablePagination.pageSize = event.pageSize;

		this.performSearch(event.pageIndex);
	}

	private initializeTableFilters(): void {
		this.tableFilters = [
			{
				field: 'uploadedDocumentType',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsDocType', 'client'),
			},
			{
				field: 'uploadedDocumentNumber',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsNumber', 'client'),
			},
			{
				field: 'uploadedDocumentBuyer',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsBuyerVat', 'client'),
			},
			{
				field: 'uploadedDocumentSupplier',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsSupplierVat', 'client'),
			},
		];
	}

	private toValidationViewModel(record: UploadDocumentsResult): DocumentRecordValidationViewModel {
		const validationData = record as DocumentRecordValidationViewModel;
		validationData.ShowExtendedData = false;
		return validationData;
	}

	protected redirectToDocumentsStep2(): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step2',
			DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!),
			this.fileRelatedContractId,
		]);
	}

	protected redirectToDocuments(): void {
		this.router.navigate(['/private/documents']);
	}

	protected showValidationResultsForConfirming(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Confirming;
	}

	protected showValidationResultsForFactoring(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Factoring;
	}

	private canAccessStep4(): boolean {
		return (
			!!this.loggedUser &&
			!this.loggedUser.IsSupportOperator &&
			!this.loggedUser.IsBackOfficeOperator &&
			(this.loggedUser.canUploadDocumentsForConfirming() || this.loggedUser.canUploadDocumentsForFactoring())
		);
	}

	protected buildRestrictionCriteria(): UploadDocumentsCountCriteria {
		return {
			fileUploadId: this.uploadId,
			validationStatusList: [UploadDocumentsValidationStatus.Invalid],
			uploadedDocumentType: this.searchCriteria.uploadedDocumentType,
			uploadedDocumentNumber: this.searchCriteria.uploadedDocumentNumber,
			uploadedDocumentSupplier: this.searchCriteria.uploadedDocumentSupplier,
			uploadedDocumentBuyer: this.searchCriteria.uploadedDocumentBuyer,
		};
	}
}
